import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { PageHeader } from "../components/Surface";
import { StatusLabel } from "../components/ListTable";
import { usePagedView, ViewToggle, Pagination } from "../components/Paged";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import { listAccounts, setRole, setAccountStatus, type AdminAccount } from "../lib/admin";
import { ROLE_LABELS, can } from "../lib/roles";

const ROLES = ["super_admin", "admin", "moderator", "support", "staff"] as const;
const PRIVILEGED = new Set(["super_admin", "admin"]);

export function AdminUsers() {
  const { roles, account } = useAuth();
  const isAdmin = can(roles, "admin_users");
  const [accounts, setAccounts] = useState<AdminAccount[]>([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  async function refresh() {
    if (!isAdmin) return;
    setAccounts((await listAccounts()).accounts);
  }
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  async function toggleRole(a: AdminAccount, role: string) {
    setBusy(a.id + role);
    try {
      await setRole(a.id, role, a.roles.includes(role) ? "revoke" : "grant");
      await refresh();
    } finally {
      setBusy(null);
    }
  }

  async function toggleStatus(a: AdminAccount) {
    setBusy(a.id + "status");
    try {
      await setAccountStatus(a.id, a.status === "suspended" ? "active" : "suspended");
      await refresh();
    } finally {
      setBusy(null);
    }
  }

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? accounts.filter((a) => (a.email ?? "").toLowerCase().includes(s) || a.id.includes(s)) : accounts;
  }, [accounts, q]);
  const { view, page, pageCount, pageItems, setView, setPage } = usePagedView(filtered, 15, "list");

  const controls = (a: (typeof accounts)[number]) => {
    const isSelf = a.id === account?.id;
    const iAmSuper = roles.includes("super_admin");
    return (
      <div className="flex flex-wrap justify-end gap-1.5">
        {ROLES.map((role) => {
          const locked = isSelf || (PRIVILEGED.has(role) && !iAmSuper);
          return (
            <Button key={role} variant="ghost" disabled={busy === a.id + role || locked} onClick={() => toggleRole(a, role)}>
              {a.roles.includes(role) ? `− ${ROLE_LABELS[role]}` : `+ ${ROLE_LABELS[role]}`}
            </Button>
          );
        })}
        <Button variant="ghost" disabled={busy === a.id + "status" || isSelf} onClick={() => toggleStatus(a)}>
          <span className={a.status === "suspended" ? "text-verified" : "text-danger"}>{a.status === "suspended" ? "Reactivate" : "Suspend"}</span>
        </Button>
      </div>
    );
  };

  if (!isAdmin) {
    return (
      <div className="grid gap-6">
        <PageHeader title="Users & roles" subtitle="Admin access required." />
        <p className="font-mono text-sm text-danger">Admins only.</p>
      </div>
    );
  }

  return (
    <div className="grid gap-6">
      <PageHeader title="Admin · Users" subtitle={`${accounts.length} accounts. Manage roles and access.`} />

      <div className="flex items-center justify-between gap-2">
        <input className={`${inputClass} max-w-sm`} placeholder="Search email or ID…" aria-label="Search users" value={q} onChange={(e) => setQ(e.target.value)} />
        <ViewToggle view={view} onChange={setView} />
      </div>

      {filtered.length === 0 ? (
        <p className="font-mono text-sm text-muted">No matching accounts.</p>
      ) : view === "list" ? (
        <div className="overflow-x-auto rounded-card border border-line bg-surface">
          <div className="min-w-[760px]">
            <div className="grid grid-cols-[minmax(0,1fr)_110px_110px_120px_auto] items-center gap-4 border-b border-line px-5 py-3 text-[11px] font-bold uppercase tracking-widest text-muted">
              <span>Account</span><span>Identity</span><span>Status</span><span>Joined</span><span className="justify-self-end">Actions</span>
            </div>
            {pageItems.map((a) => (
              <div key={a.id} className="grid grid-cols-[minmax(0,1fr)_110px_110px_120px_auto] items-center gap-4 border-t border-line px-5 py-4">
                <div className="grid min-w-0">
                  <span className="truncate font-mono text-sm font-bold">{a.email ?? "—"}</span>
                  <span className="truncate font-mono text-[10px] text-muted">{a.id.slice(0, 8)}</span>
                </div>
                <StatusLabel status={a.identity_verified ? "verified" : "pending"} />
                <StatusLabel status={a.status} />
                <span className="font-mono text-xs text-muted">{new Date(a.created_at).toLocaleDateString()}</span>
                {controls(a)}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {pageItems.map((a) => (
            <div key={a.id} className="grid gap-3 rounded-card border border-line bg-surface p-4">
              <div className="grid min-w-0">
                <span className="truncate font-mono text-sm font-bold">{a.email ?? "—"}</span>
                <span className="truncate font-mono text-[10px] text-muted">{a.id.slice(0, 8)} · joined {new Date(a.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex gap-2"><StatusLabel status={a.identity_verified ? "verified" : "pending"} /><StatusLabel status={a.status} /></div>
              {controls(a)}
            </div>
          ))}
        </div>
      )}
      <Pagination page={page} pageCount={pageCount} onPage={setPage} />
    </div>
  );
}
